import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import { useScreenPermissions } from '../components/useScreenPermissions';

import SalesOrderViewModal from './SalesOrderViewModal';
import MaterialReceiptViewModal from './MaterialReceiptViewModal';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const TrackDocumentIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

const HidePanelIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

// Modern Error Alert Icon
const ErrorAlertIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" stroke="currentColor" fill="none" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// Document type icons
const SalesOrderIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

const PurchaseOrderIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2l3.54 6.54-2.32 2.32L6 22h12l-1.22-11.14-2.32-2.32L18 2H6z"></path>
    <line x1="12" y1="22" x2="12" y2="16"></line>
    <line x1="9" y1="16" x2="15" y2="16"></line>
  </svg>
);

const MRNIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

// Modern Error Popup Component
const ErrorPopup = ({ message, isVisible, onClose }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 4000); // Auto-close after 4 seconds
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop without blur */}
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose}></div>
      
      {/* Modern Popup Card */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full transform transition-all duration-300 animate-fadeIn">
        {/* Decorative top border */}
        <div className="h-1 w-full bg-gradient-to-r from-red-500 to-red-600 rounded-t-2xl"></div>
        
        <div className="p-6">
          <div className="flex items-start">
            {/* Icon with gradient background */}
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
                <ErrorAlertIcon className="h-6 w-6 text-white" />
              </div>
            </div>
            
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-bold text-gray-900">Access Denied</h3>
              <div className="mt-2">
                <p className="text-sm text-gray-600">{message}</p>
              </div>
            </div>
            
            {/* Close button */}
            <div className="ml-4 flex-shrink-0 flex">
              <button
                className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                onClick={onClose}
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 border border-transparent rounded-lg shadow-md hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all"
              onClick={onClose}
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const API_URL = '/api';

// PurchaseOrderViewModal Component with enhanced tree structure
const PurchaseOrderViewModal = ({ open, onClose, poId }) => {
  const [poDetails, setPoDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    vendors: [],
    locations: [],
    branches: [],
  });
  
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState(null);
  const [showTrackingPanel, setShowTrackingPanel] = useState(false);
  
  // State for document modals
  const [activeDocumentTab, setActiveDocumentTab] = useState(null);

  // State for error popup
  const [errorPopup, setErrorPopup] = useState({
    isVisible: false,
    message: ''
  });

  const getAuthHeaders = (isFormData = false) => {
  const token = localStorage.getItem('authToken'); // Or 'authToken' depending on your login logic
  
  const headers = {
    // Only add Authorization if token exists
    ...(token && { 'Authorization': `Bearer ${token}` })
  };

  // Only set Content-Type to 'application/json' if we are NOT sending FormData.
  // If sending FormData, the browser MUST set the Content-Type and boundary automatically.
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};

  const LINKED_DOCUMENT_OPTIONS = [
    { value: '', label: 'Select Document Type' },
    { value: 'SO', label: 'Sales Order' },
    { value: 'PR', label: 'Purchase Requisition' },
    { value: 'FR', label: 'Forecast Requisition' },
    { value: 'RFQ', label: 'RFQ' },
  ];

  const getLinkedDocumentLabel = (value) => {
    if (!value) return '';
    const docType = Array.isArray(value) ? value[0] : value;
    const option = LINKED_DOCUMENT_OPTIONS.find(opt => opt.value === docType);
    return option ? option.label : docType || '';
  };
  
  const [isMaximized, setIsMaximized] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const SCREEN_NAME = 'PurchaseOrder';

  // Add permissions hook
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  
  // Calculate permissions for MRN and Sales Order
  const canViewMRN = hasFullAccess || hasPermission('MRN_MASTER', 'CanView');
  const canViewSales = hasFullAccess || hasPermission('SALES_ORDER', 'CanView');
  const canViewPO = hasFullAccess || hasPermission('PURCHASE_ORDER', 'CanView');

  // Function to show the error popup
  const showErrorPopup = (message) => {
    setErrorPopup({
      isVisible: true,
      message
    });
  };
  
  // Function to hide the error popup
  const hideErrorPopup = () => {
    setErrorPopup({
      isVisible: false,
      message: ''
    });
  };

  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };

  const shouldShowDepartmentOnHeader = (cfg) => {
    if (!cfg) return false;
    const showHeader = truthy(cfg.ShowOnHeader) || truthy(cfg.showOnHeader);
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    return showHeader && showScreen;
  };

  const shouldShowDepartmentOnItems = (cfg) => {
    if (!cfg) return false;
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    const showItem = truthy(cfg.ShowOnItem) || truthy(cfg.showOnItem);
    return showScreen && showItem;
  };

  const formatDepartment = (name, id, code) => {
    const toStr = (v) => (v === null || v === undefined ? '' : String(v).trim());
    const firstToken = (s) => {
      if (!s) return '';
      const token = s.split(',').map(t => t.trim()).filter(Boolean)[0] || '';
      return token;
    };

    const cleanName = toStr(name);
    const cleanIdRaw = Array.isArray(id) ? toStr(id[0]) : toStr(id);
    const cleanId = firstToken(cleanIdRaw);
    const cleanCode = Array.isArray(code) ? toStr(code[0]) : toStr(code);

    const left = [cleanName, cleanId].filter(Boolean).join(' ').trim();
    const built = [left, cleanCode].filter(Boolean).join(' , ').trim();

    return built || '-';
  };

  const fetchScreenConfig = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        let body = null;
        try { body = await res.json(); } catch (e) {}
        const msg = body?.message ? body.message : `Server returned ${res.status}`;
        throw new Error(msg);
      }
      const data = await res.json();
      const cfg = Array.isArray(data) ? (data[0] || null) : data;
      console.log('Screen info.....', cfg);
      setScreenConfig(cfg);
      return cfg;
    } catch (err) {
      console.error('fetchScreenConfig error:', err);
      setScreenConfig(null);
      return null;
    }
  };

   useEffect(() => {
    if (open) {
      setIsMaximized(false);
      setShowTrackingPanel(false);
      setActiveDocumentTab(null); 
      fetchDropdownData();
      fetchScreenConfig();
      
      // Check if poId is a string (direct number) or an object
      const poNumber = typeof poId === 'string' 
        ? poId 
        : (poId?.PurchaseOrderNumber || poId?.purchaseOrderNumber);

      if (poNumber) {
        fetchPODetails(poNumber);
        fetchTrackingData(poNumber);
      }
    }
  }, [open, poId]);
  
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  const toggleTrackingPanel = () => {
    const newState = !showTrackingPanel;
    setShowTrackingPanel(newState);
    // Maximize when tracking panel is opened
    if (newState && !isMaximized) {
      setIsMaximized(true);
    }
  };
  
  const fetchDropdownData = async () => {
    try {
      const response = await axios.get(`${API_URL}/purchase-order/form-data`,{headers: getAuthHeaders()});
      setDropdownData({
        vendors: response.data.vendors || [],
        locations: response.data.locations || [],
        branches: response.data.branches || [],
      });
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };
  
  const fetchPODetails = async (purchaseOrderNumber) => {
    setLoading(true);
    setError(null);
    try {
      console.log(`Fetching PO details for: ${purchaseOrderNumber}`);
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API_URL}/purchase-order/${encodeURIComponent(purchaseOrderNumber)}?t=${timestamp}`,{headers: getAuthHeaders()});
      console.log('PO details:', response.data);
      
      console.log('Status value (is_active):', response.data.is_active, 'Type:', typeof response.data.is_active);
      
      setPoDetails(response.data);
    } catch (error) {
      console.error('Error fetching PO details:', error);
      setError('Failed to load purchase order details');
      setPoDetails(null);
    }
    setLoading(false);
  };
  
  const fetchTrackingData = async (purchaseOrderNumber) => {
    setTrackingLoading(true);
    setTrackingError(null);
    try {
      const response = await axios.get(`${API_URL}/purchase-order-tracking/${purchaseOrderNumber}`,{headers: getAuthHeaders()});
      setTrackingData(response.data);
    } catch (error) {
      console.error('Error fetching tracking data:', error);
      setTrackingError('Failed to load tracking information');
    } finally {
      setTrackingLoading(false);
    }
  };
  
  const getVendorName = (po) => {
    return po.VendorName || po.vendor_name || po.vendorName || '-';
  };
  
  const getLocationName = (locationCode) => {
    if (!locationCode) return '-';
    const location = dropdownData.locations.find(loc => loc.LocationCode === locationCode);
    return location ? location.LocationName : locationCode;
  };
  
  const getBranchInfo = (branchCode) => {
    if (!branchCode) return '-';
    const branch = dropdownData.branches.find(branch => branch.BranchCode === branchCode);
    return branch ? `${branch.BranchCode}-${branch.BranchName}` : branchCode;
  };
  
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return parseFloat(value).toFixed(2);
  };
  
  if (!open || !poId) return null;
  
  const po = poDetails || poId;
  const poItems = poDetails?.Items || poId?.Items || [];
  
  const isActive = poId.is_active === 1 || poId.is_active === true;
  const statusText = isActive ? 'Approved' : 'Pending';
  const statusStyle = isActive 
    ? 'bg-green-100 text-green-800' 
    : 'bg-yellow-100 text-yellow-800';

    console.log('PurchaseOrderViewModal - isActive:', isActive);
  
  // Enhanced Tree Structure Component
 // Enhanced Tree Structure Component
const TrackingTree = ({ data}) => {
  if (!data) return null;
  
  const { linkedSalesOrders, purchaseOrder, mrns } = data;
  
  // Add debugging
  console.log('TrackingTree - isActive:', isActive);
  console.log('TrackingTree - mrns:', mrns);
  
  const StatusBadge = ({ status }) => {
    const statusStyle = status === 'Completed' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-yellow-100 text-yellow-800';
      
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${statusStyle}`}>
        {status}
      </span>
    );
  };
  
  // Enhanced Document Node Component with click handling
  const DocumentNode = ({ 
    type, 
    number, 
    date, 
    status, 
    isPending = false,
    isCenter = false,
    onClick
  }) => {
    const getIcon = () => {
      switch(type) {
        case 'sales':
          return <SalesOrderIcon className="text-white" />;
        case 'purchase':
          return <PurchaseOrderIcon className="text-white" />;
        case 'mrn':
          return <MRNIcon className={isPending ? "text-gray-400" : "text-white"} />;
        default:
          return null;
      }
    };
    
    const getColorClass = () => {
      if (isPending) return 'bg-gray-200';
      switch(type) {
        case 'sales':
          return 'bg-purple-500' ;
        case 'purchase':
          return 'bg-blue-500';
        case 'mrn':
          return 'bg-green-500';
        default:
          return 'bg-gray-500';
      }
    };
    
    const isClickable = (type === 'sales' && canViewSales && !isPending) || 
                       (type === 'mrn' && canViewMRN && !isPending) || (type === 'purchase' && canViewPO && !isPending);
    
    const handleClick = () => {
      if (isClickable && onClick) {
        onClick(type, number);
      } else if (!isClickable && !isPending) {
        if (type === 'sales') {
          showErrorPopup("You don't have permission to view Sales Orders");
        } else if (type === 'mrn') {
          showErrorPopup("You don't have permission to view MRNs");
        }
      }
    };
    
    return (
      <div 
        className={`flex flex-col items-center mt-4 cursor-pointer hover:opacity-80 transition-opacity`}
        onClick={handleClick}
        title={isClickable ? `View ${type === 'sales' ? 'Sales Order' : 'MRN'} in new tab` : 
              isPending ? 'Not created yet' : 'No permission to view'}
      >
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getColorClass()} $`}>
          {getIcon()}
        </div>
        <div className={`mt-2 text-center ${isPending ? 'text-gray-400' : 'text-gray-800'}`}>
          <div className="font-medium text-sm">
            {type === 'sales' && 'Sales Order'}
            {type === 'purchase' && 'Purchase Order'}
            {type === 'mrn' && 'MRN'}
          </div>
          <div className="font-semibold">{number}</div>
          {date && !isPending && (
            <div className="text-xs text-gray-500 mt-1">
              {dayjs(date).format('MMM DD, YYYY')}
            </div>
          )}
          {status && !isPending && <StatusBadge status={status} className="mt-2" />}
          {isPending && (
            <div className="text-xs text-gray-400 italic mt-2">Not created yet</div>
          )}
          {!isClickable && !isPending && (
            <div className="text-xs text-red-500 italic mt-1">No permission</div>
          )}
        </div>
      </div>
    );
  };
  
  // Flowchart-style connector components
  const SalesToPoConnector = ({ count }) => {
    return (
      <div className="relative w-full h-16 flex items-center justify-center">
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Vertical lines from each sales order */}
          {[...Array(count)].map((_, i) => {
            const x = (i + 1) * (100 / (count + 1));
            return (
              <line 
                key={`sales-line-${i}`}
                x1={`${x}%`} 
                y1="0" 
                x2={`${x}%`} 
                y2="30" 
                stroke="#4B5563" 
                strokeWidth="0.3"
              />
            );
          })}
          
          {/* Horizontal connecting line */}
          <line 
            x1={`${100 / (count + 1)}%`} 
            y1="30" 
            x2={`${count * 100 / (count + 1)}%`} 
            y2="30" 
            stroke="#4B5563" 
            strokeWidth="1"
          />
          
          {/* Vertical line to PO */}
          <line 
            x1="50%" 
            y1="30" 
            x2="50%" 
            y2="100" 
            stroke="#4B5563" 
            strokeWidth="0.3"
          />
          
          {/* Arrowhead pointing to PO */}
           <polygon
          points="50,100 47,93 53,93" 
          fill="#4B5563" 
        />
        </svg>
      </div>
    );
  };
  
  const PoToMrnConnector = ({ count }) => {
    return (
      <div className="relative w-full h-16 flex items-center justify-center">
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Vertical line from PO */}
          <line 
            x1="50%" 
            y1="0" 
            x2="50%" 
            y2="30" 
            stroke="#4B5563" 
            strokeWidth="0.3"
          />
          
          {/* Horizontal connecting line */}
          <line 
            x1={`${100 / (count + 1)}%`} 
            y1="30" 
            x2={`${count * 100 / (count + 1)}%`} 
            y2="30" 
            stroke="#4B5563" 
            strokeWidth="1"
          />
          
          {/* Vertical lines to each MRN */}
          {[...Array(count)].map((_, i) => {
            const x = (i + 1) * (100 / (count + 1));
            return (
              <React.Fragment key={`mrn-line-${i}`}>
                <line 
                  x1={`${x}%`} 
                  y1="30" 
                  x2={`${x}%`} 
                  y2="70" 
                  stroke="#4B5563" 
                  strokeWidth="0.3"
                />
                {/* Arrowhead for each MRN */}
                <polygon 
              points={`${x},70 ${parseFloat(x) - 1.5},63 ${parseFloat(x) + 1.5},63`} 
              fill="#4B5563" 
            />
              </React.Fragment>
            );
          })}
        </svg>
      </div>
    );
  };
  
  // Handle document click
  const handleDocumentClick = (docType, docNumber) => {
     console.log(`Document clicked: ${docType}, Number: ${docNumber}`);
    if (docType === 'sales') {
      const newTab = {
        type: 'sales',
        SalesOrderNumber: docNumber,
        salesOrderNumber: docNumber,
      };
      console.log('Setting activeDocumentTab to:', newTab);
      setActiveDocumentTab(newTab);
    }else if (docType === 'mrn') {
      setActiveDocumentTab({
        type: 'mrn',
        number: docNumber,
        mrnNumber: docNumber, // Add both properties for compatibility
        MRNNumber: docNumber
      });
    }
  };
  
  return (
    <div className="bg-white h-full">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-[#384959]">Document Tracking</h3>
        <button
          onClick={() => setShowTrackingPanel(false)}
          className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
          title="Hide Panel"
        >
          <HidePanelIcon size={20} />
        </button>
      </div>
      
      {trackingLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading tracking information...</p>
          </div>
        </div>
      ) : trackingError ? (
        <div className="bg-yellow-50 text-yellow-700 p-4 rounded-lg mb-6 border border-yellow-100">
          Tracking Error: {trackingError}
        </div>
      ) : data ? (
        <div className="flex flex-col items-center">
          {/* Sales Orders Section */}
          {linkedSalesOrders && linkedSalesOrders.length > 0 && (
            <div className="w-full">
              <div className="flex justify-center space-x-8 mb-2">
                {linkedSalesOrders.map(so => (
                  <DocumentNode 
                    key={so.salesOrderNumber}
                    type="sales"
                    number={so.salesOrderNumber}
                    date={so.salesOrderDate}
                    onClick={handleDocumentClick}
                  />
                ))}
              </div>
              
              {/* Flowchart connector from Sales Orders to Purchase Order */}
              <SalesToPoConnector count={linkedSalesOrders.length} />
            </div>
          )}
          
          {/* Purchase Order Section */}
          <DocumentNode 
            type="purchase"
            number={purchaseOrder.purchaseOrderNumber}
            date={purchaseOrder.purchaseOrderDate}
            status={purchaseOrder.status}
            isCenter={true}
          />
          
          {/* MRNs Section - only show if PO is not active */}
          {!isActive ? (
            <>
              {/* Flowchart connector from Purchase Order to MRNs */}
              {mrns && mrns.length > 0 ? (
                <PoToMrnConnector count={mrns.length} />
              ) : (
                <div className="relative w-full h-16 flex items-center justify-center">
                  <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <line 
                      x1="50%" 
                      y1="0" 
                      x2="50%" 
                      y2="70" 
                      stroke="#4B5563" 
                      strokeWidth="2"
                    />
                    <polygon 
                      points="50,70 45,60 55,60" 
                      fill="#4B5563" 
                    />
                  </svg>
                </div>
              )}
              
              {/* MRN nodes */}
              {mrns && mrns.length > 0 ? (
                <div className="flex justify-center space-x-8 mt-2">
                  {mrns.map(mrn => (
                    <DocumentNode 
                      key={mrn.mrnNumber}
                      type="mrn"
                      number={mrn.mrnNumber}
                      date={mrn.mrnDate}
                      status={typeof mrn.status === 'boolean' ? (mrn.status ? "Completed" : "Pending") : mrn.status}
                      onClick={handleDocumentClick}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex justify-center">
                  <DocumentNode 
                    type="mrn"
                    number="MRN"
                    isPending={true}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="mt-4 text-center text-gray-500 text-sm">
              PO is approved
            </div>
          )}
        </div>
      ) : (
        <div className="text-center text-gray-500 py-8">
          No tracking data available
        </div>
      )}
    </div>
  );
};
  
  console.log('Status determination - is_active:', poId.is_active, 'Type:', typeof poId.is_active, 'isActive:', isActive, 'statusText:', statusText);
  
  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${
        open ? "opacity-100 visible" : "opacity-0 invisible"
      }`}
    >
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out forwards;
          }
        `}
      </style>
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>
      
      {/* Main Modal Container */}
      <div className={`bg-white shadow-2xl flex flex-col transition-all duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} ${isMaximized ? 'w-full h-full max-w-none m-0 rounded-none' : showTrackingPanel ? 'w-[90%] max-w-7xl' : 'w-full max-w-6xl'} h-full rounded-none`}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">
              Purchase Order Details
            </h2>
            <div className="flex gap-2">
              {/* <button 
                className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
                  showTrackingPanel 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                }`}
                onClick={toggleTrackingPanel}
                title="Track Document"
              >
                <TrackDocumentIcon size={18} />
                <span>Track Document</span>
              </button> */}
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={24} /> : <MaximizeIcon size={24} />}
              </button>
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1"
                onClick={onClose}
                title="Close"
              >
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Main Content Area with Sidebar Layout */}
        <div className="flex-grow overflow-hidden flex">
          {/* Main PO Details Content */}
          <div className={`flex-grow overflow-y-auto hide-scrollbar px-8 ${showTrackingPanel ? 'pr-4' : ''}`}>
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading purchase order details...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
                Error: {error}
              </div>
            ) : (
              <div className="space-y-6 pb-6">
                {/* PO Header */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">
                    Purchase Order Information
                  </h3>
                  
                  {/* First row: PO Number and PO Date */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        PO Number
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {po.PurchaseOrderNumber || po.purchaseOrderNumber || '-'}
                      </div>
                    </div>
                    {/* <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        PO Date
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {po.PurchaseOrderDate ? dayjs(po.PurchaseOrderDate).format('YYYY-MM-DD') : '-'}
                      </div>
                    </div> */}
                  </div>
                  
                  {/* Second row: Vendor Code and Vendor Name */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Vendor Code
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {po.VendorCode || po.vendorCode || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Vendor Name
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {getVendorName(po)}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Location Code
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {getLocationName(po.LocationCode || po.locationCode)}
                      </div>
                    </div>
                    
                      <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Location Name
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {po.LocationName || po.LocationName || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Branch Code
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {po.BranchCode || po.branchCode || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Branch Name
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {getLocationName(po.BranchName )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Project Code
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {po.ProjectCode}
                      </div>
                    </div><div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Feeder Code
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {po.FeederCode} - {po.FeederDescription}
                      </div>
                    </div>

                  </div>
                  
                 
                </div>
                
                {/* PO Items */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-[#384959]">
                      Ordered Items
                    </h3>
                  </div>
                  <div className="overflow-x-auto custom-scrollbar">
                    <table className="min-w-full border border-[#E2E8F0]">
                      <thead>
                        <tr className="bg-[#BDDDFC] text-[#384959]">
                          <th className="p-2">#</th>
                          <th className="p-2">Product Code</th>
                          <th className="p-2">Product Name</th>
                          {/* <th className="p-2">Description</th> */}
                          <th className="p-2">UOM</th>
                          <th className="p-2">Quantity</th>
                          <th className="p-2">Project Code</th>
                          {/* <th className="p-2">Remaining Qty</th> */}
                          {/* <th className="p-2">Unit Price</th>
                          <th className="p-2">Discount</th> */}
                          {/* <th className="p-2">Tax</th> */}
                          {/* <th className="p-2">Tax Rate</th> */}
                          {/* <th className="p-2">Tax Amount</th> */}
                          {/* {shouldShowDepartmentOnItems(screenConfig || po?.screenConfig) && (
                            <th className="p-2">Department</th>
                          )} */}
                          {/* <th className="p-2">Total</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {poItems.length > 0 ? (
                          poItems.map((item, idx) => {
                            const taxRate = item.TaxPercentage ? parseFloat(item.TaxPercentage) : 0;
                            
                            return (
                              <tr key={idx} className="border-t border-[#E2E8F0]">
                                <td className="p-2 text-center">{idx + 1}</td>
                                <td className="p-2">
                                  <div className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {item.ProductCode || item.productCode || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {item.ProductName || item.productName || '-'}
                                  </div>
                                </td>
                                {/* <td className="p-2">
                                  <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {item.Description || item.description || '-'}
                                  </div>
                                </td> */}
                                <td className="p-2">
                                  <div className="w-20 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {item.UOM || item.uom || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {item.Quantity || item.quantity || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {item.ProjectCode || '-'}
                                  </div>
                                </td>
                                {/* <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {item.RemainingQty || '-'}
                                  </div>
                                </td> */}
                                {/* <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {formatCurrency(item.UnitPrice || item.unitPrice)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {formatCurrency(item.Discount || item.discount)}
                                  </div>
                                </td> */}
                                {/* <td className="p-2">
                                  <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {item.TaxName || item.taxName || item.TaxCode || item.taxCode || '-'}
                                  </div>
                                </td> */}
                                {/* <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {taxRate}%
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {formatCurrency(item.TaxAmount || item.taxAmount)}
                                  </div>
                                </td> */}
                                {/* {shouldShowDepartmentOnItems(screenConfig || po?.screenConfig) && (
                                  <td className="p-2">
                                    <div className="w-40 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                      {formatDepartment(item.DepartmentCode)}
                                    </div>
                                  </td>
                                )} */}
                                {/* <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] font-semibold">
                                    {formatCurrency(item.Total || item.total)}
                                  </div>
                                </td> */}
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan={shouldShowDepartmentOnItems(screenConfig || po?.screenConfig) ? 14 : 13}
                              className="text-center text-gray-500 py-4"
                            >
                              No items found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                 
                </div>
              </div>
            )}
          </div>
          
          {/* Tracking Sidebar */}
          {showTrackingPanel && (
            <div className="border-l  overflow-y-auto hide-scrollbar bg-white rounded-xl p-6 border border-gray-200 h-full" style={{width : "50vw"}}>
              <TrackingTree data={trackingData} />
            </div>
          )}
        </div>
        
        {/* Fixed Footer with Button */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
      
      {/* Document Tab Modals */}
      {activeDocumentTab && activeDocumentTab.type === 'sales' && (
        <SalesOrderViewModal
          open={!!activeDocumentTab}
          onClose={() => setActiveDocumentTab(null)}
          soId={activeDocumentTab} // Pass the entire object with both properties
        />
      )}
      
      {activeDocumentTab && activeDocumentTab.type === 'mrn' && (
        <MaterialReceiptViewModal
          open={!!activeDocumentTab}
          onClose={() => setActiveDocumentTab(null)}
          mrnId={activeDocumentTab} // Pass the entire object with both properties
        />
      )}
      
      {/* Modern Error Popup */}
      <ErrorPopup 
        message={errorPopup.message} 
        isVisible={errorPopup.isVisible} 
        onClose={hideErrorPopup} 
      />
    </div>
  );
};

export default PurchaseOrderViewModal;